import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import BottomTab from "src/components/BottomTab";
import Header from "src/components/Header";
import Footer from "src/components/Footer"; 
import ItineraryItemView from "src/components/ItineraryItemView";
import { ItineraryItemSchema, TripItemType } from "src/utils/ApiSchema";
import { getWelcomeMessage } from "src/utils/greetingMessage";

const popupVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.95 },
};

function WaitlistPopup({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [location, setLocation] = useState("");
  const [deviceInfo, setDeviceInfo] = useState({
    platform: "Unknown",
    browser: "Unknown",
  });

  const navigate = useNavigate();

  useEffect(() => {
    fetch("https://ipapi.co/json/")
      .then((res) => res.json())
      .then((data) => {
        const loc = `${data.city}, ${data.region}, ${data.country_name}`;
        setLocation(loc);
      })
      .catch(() => setLocation("Unknown"));

    const userAgent = navigator.userAgent.toLowerCase();

    // Detect platform
    const isMobile =
      /android|iphone|ipad|ipod|blackberry|windows phone|mobile/i.test(
        userAgent
      );
    const platform = isMobile ? "Mobile" : "Desktop";

    // Detect browser
    let browser = "Unknown";
    if (userAgent.includes("chrome") && !userAgent.includes("edge"))
      browser = "Chrome";
    else if (userAgent.includes("safari") && !userAgent.includes("chrome"))
      browser = "Safari";
    else if (userAgent.includes("firefox")) browser = "Firefox";
    else if (userAgent.includes("edg")) browser = "Edge";
    else if (userAgent.includes("opera") || userAgent.includes("opr"))
      browser = "Opera";

    setDeviceInfo({ platform, browser });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const formUrl =
      "https://docs.google.com/forms/d/e/1FAIpQLSfBVmbofSW3ol3uaQRrBF8czd4BDR_JCh8tV36WIpRCxQ-_fw/formResponse";
    const formData = new FormData();
    formData.append("entry.96017419", name);
    formData.append("entry.1491545790", email);
    formData.append("entry.742914215", location);
    formData.append("entry.328400740", deviceInfo.platform);
    formData.append("entry.64961969", deviceInfo.browser);

    await fetch(formUrl, {
      method: "POST",
      mode: "no-cors",
      body: formData,
    }).catch((error) => {
      console.error("Error submitting form:", error);
    });

    await navigate("/waitlist-confirmed");
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div
          onClick={onClose}
          className="fixed inset-0 bg-[rgba(0,0,0,0.5)]"
          style={{
            backdropFilter: "blur(5px)",
            boxShadow: "0px 4px 162.7px 0px rgba(255, 255, 255, 0.12) inset",
          }}
        />

        <motion.div
          id="waitlist-popup"
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={popupVariants}
          transition={{ duration: 0.3 }}
          className="relative z-50 bg-white text-white rounded-3xl shadow-2xl w-full max-w-xl m-4 md:m-0 p-4 md:p-12 space-y-4"
        >
          <button
            id="waitlist-popup-close-btn"
            onClick={onClose}
            aria-label="Close waitlist popup"
            className="absolute top-2 right-5 text-gray-400 hover:text-white text-xl"
          >
            ×
          </button>

          <form id="waitlist-form" onSubmit={handleSubmit}>
            <div className="pb-12 flex items-center justify-center flex-col">
              <h1 className="text-black text-3xl font-bold text-center">
                We are almost there
              </h1>
              <p className="text-md text-gray-400 text-center">
                Be the first to know when we launch! ☝🏾
              </p>
            </div>

            <div className="flex flex-col space-y-4">
              <input
                id="waitlist-name-input"
                type="text"
                name="name"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="px-4 h-[55px] rounded-full bg-gray-200 text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600"
                required
                autoComplete="name"
                inputMode="text"
                aria-label="Your name"
              />

              <input
                id="waitlist-email-input"
                type="email"
                name="email"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="px-4 h-[55px] rounded-full bg-gray-200 text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600"
                required
                autoComplete="email"
                inputMode="email"
                aria-label="Your email"
              />
            </div>

            <button
              id="waitlist-submit-btn"
              type="submit"
              className="w-full mt-4 bg-black text-white font-bold h-[55px] rounded-full hover:bg-gray-600 transition"
            >
              Join Waitlist
            </button>
            <div className="pt-6 pb-6 flex items-center justify-center flex-col">
              <p className="text-md text-gray-400 text-center">
                (you&apos;ll be notified when you gain access)
              </p>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}

function Waitlist() {
  const [showPopup, setShowPopup] = useState(false);

  const itineraries: ItineraryItemSchema[] = [
    {
      id: "1",
      createdId: "1",
      name: "Our 8yr anniversary ❤️",
      completed: false,
      location: "Marrakech, Morocco",
      time: "2026-10-02T10:00:00Z",
      cost: {
        totalValue: 1305.12,
        currency: "£",
        userValue: 1305.12,
        userCurrency: "£",
      },
      trips: [],
    },
    {
      id: "2",
      createdId: "2",
      name: "Family trip to New Zealand",
      completed: false,
      location: "Queenstown, New Zealand",
      time: "2027-02-13T10:00:00Z",
      cost: {
        totalValue: 6980.46,
        currency: "$",
        userValue: 6980.46,
        userCurrency: "$",
      },
      trips: [],
    },
    {
      id: "3",
      createdId: "3",
      name: "Wine tour 🍷",
      completed: true,
      location: "Sardinia, Italy",
      time: "2025-05-01T10:00:00Z",
      cost: {
        totalValue: 1773.95,
        currency: "€",
        userValue: 1773.95,
        userCurrency: "€",
      },
      trips: [],
    },
        {
      id: "4",
      createdId: "4",
      name: "Trip to Athens",
      completed: true,
      location: "Athens, Greece",
      time: "2025-08-22T10:00:00Z",
      cost: {
        totalValue: 609.45,
        currency: "€",
        userValue: 609.45,
        userCurrency: "€",
      },
      trips: [],
    },
  ];

  return (
    <>
      <div className="relative z-10 h-full w-full flex flex-col items-center justify-center mx-[16px]">
        <div className="hidden w-full items-center justify-center md:flex px-[15%]">
          <Header
            isUserLoggedIn={true}
            onPress={() => setShowPopup(true)}
          />
        </div>

        <>
          <p className="flex text-white font-semiBold text-xl text-center mt-[64px] md:pl-[32px] md:pr-[32px] md:mt-[32px] md:text-xxl">
            {getWelcomeMessage(null, null)}
          </p>
          <div className="flex flex-col w-full h-full mb-[96px] items-center justify-center overflow-scroll md:w-[500px] md:mt-[12px] md:mb-[0px]">
            <div className="relative flex w-full h-full flex-col items-center">
              {itineraries
                .sort((a, b) =>
                  a.completed === b.completed ? 0 : a.completed ? 1 : -1
                )
                .map((item) => (
                  <ItineraryItemView
                    type={TripItemType.Transparent}
                    itinerary={item}
                    key={item.id}
                    onClick={() => setShowPopup(true)}
                    data-itinerary-id={item.id} // data attribute for analytics
                  />
                ))}
            </div>
          </div>
        </>
      </div>

      <div className="relative w-full">
        <BottomTab />
        <BottomTab onPress={() => setShowPopup(true)} />
      </div>

      <Footer />

      {showPopup && <WaitlistPopup onClose={() => setShowPopup(false)} />}
    </>
  );
}

export default Waitlist;
